import { useState } from 'react';
import type { DocumentMeta } from '../../core/types';
import { applyCanvasSize, type CanvasAnchor } from '../../state/actions';
import { Modal, NumberInput } from '../components/controls';

const ANCHORS: CanvasAnchor[][] = [
  ['topLeft', 'top', 'topRight'],
  ['left', 'center', 'right'],
  ['bottomLeft', 'bottom', 'bottomRight'],
];

const ARROWS: Record<CanvasAnchor, string> = {
  topLeft: '↖',
  top: '↑',
  topRight: '↗',
  left: '←',
  center: '•',
  right: '→',
  bottomLeft: '↙',
  bottom: '↓',
  bottomRight: '↘',
};

export function CanvasSizeDialog({
  doc,
  open,
  onClose,
}: {
  doc: DocumentMeta;
  open: boolean;
  onClose: () => void;
}) {
  const [width, setWidth] = useState(doc.width);
  const [height, setHeight] = useState(doc.height);
  const [anchor, setAnchor] = useState<CanvasAnchor>('center');

  const apply = () => {
    applyCanvasSize(width, height, anchor);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Canvas Size"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button button--primary" onClick={apply}>
            Apply
          </button>
        </>
      }
    >
      <p className="dialog__note">
        Current size: {doc.width} × {doc.height} px. Pixels are not resampled.
      </p>
      <div className="field">
        <span className="field__label" style={{ width: 66 }}>
          Width
        </span>
        <NumberInput value={width} min={1} max={16384} onChange={setWidth} width={90} />
        <span className="field__label">px</span>
      </div>
      <div className="field">
        <span className="field__label" style={{ width: 66 }}>
          Height
        </span>
        <NumberInput value={height} min={1} max={16384} onChange={setHeight} width={90} />
        <span className="field__label">px</span>
      </div>

      <div className="field field--stack">
        <span className="field__label">Anchor</span>
        <div className="anchor-grid">
          {ANCHORS.flat().map((id) => (
            <button
              key={id}
              type="button"
              className={`anchor-grid__cell${anchor === id ? ' anchor-grid__cell--active' : ''}`}
              onClick={() => setAnchor(id)}
              title={id}
            >
              {ARROWS[id]}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
